// Pure PIN scrubbing for error-report breadcrumbs (lib/bugsnag.ts
// onBreadcrumb). No imports so the node:test file next to it runs it
// directly.
//
// Credentials in breadcrumbs. Network breadcrumbs record the request URL
// (and some libraries' request metadata), so a PIN on a query string --
// the legacy `check-manager-access?pin=` form, or anything else -- would be
// shipped with the next error report. Scrub any `pin` / `*_pin` value
// (override_pin, gift_card_pin, ...) from the message, from metadata keys
// named like one, and from query strings / JSON bodies inside metadata
// strings (a manual leaveBreadcrumb with a request body, say).
// `pin=` at the very start, after ?/&/; or whitespace ("POST /x pin=1").
const PIN_PARAM = /((?:^|[?&;\s])(?:[a-z_]*_)?pin=)[^&#;\s]*/gi
// "pin": "1234" / "override_pin": 1234
const PIN_JSON = /("(?:[a-z_]*_)?pin"\s*:\s*)("(?:[^"\\]|\\.)*"|[^,}\]\s]+)/gi
// The same inside an escaped JSON string: \"override_pin\":\"1234\"
const PIN_JSON_ESCAPED = /(\\"(?:[a-z_]*_)?pin\\"\s*:\s*)(\\"(?:[^\\]|\\(?!"))*\\"|[^,}\]\s\\]+)/gi
// Key names: pin, override_pin, gift_card_pin -- not spin, pinned, shipping.
const PIN_KEY = /(^|_)pin$/i

export function scrubPinText(text: string): string {
  return text
    .replace(PIN_PARAM, '$1[REDACTED]')
    .replace(PIN_JSON, '$1"[REDACTED]"')
    .replace(PIN_JSON_ESCAPED, '$1\\"[REDACTED]\\"')
}

/** True for a metadata key that names a PIN. */
export function isPinKey(key: string): boolean {
  return PIN_KEY.test(key)
}

function scrubValue(v: unknown): unknown {
  if (typeof v === 'string') return scrubPinText(v)
  // Arrays are copied, never written into: metadata can hold frozen
  // snapshots (store state), and an in-place write there throws, which
  // would drop the whole breadcrumb.
  if (Array.isArray(v)) return v.map(scrubValue)
  if (v && typeof v === 'object') {
    const obj = Object.isFrozen(v) ? { ...(v as Record<string, unknown>) } : v as Record<string, unknown>
    scrubBreadcrumbMetadata(obj)
    return obj
  }
  return v
}

/** Scrub in place: PIN-named keys are redacted, strings scrubbed, and
 *  nested objects and arrays walked. */
export function scrubBreadcrumbMetadata(meta: Record<string, unknown>): void {
  for (const [k, v] of Object.entries(meta)) {
    meta[k] = isPinKey(k) ? '[REDACTED]' : scrubValue(v)
  }
}

/** A copy of `value` with every PIN-named key redacted, at any depth
 *  (api/client's DEV request logging). The input is never modified. */
export function redactPins<T>(value: T): T {
  if (Array.isArray(value)) return value.map(redactPins) as unknown as T
  if (value && typeof value === 'object' && Object.getPrototypeOf(value) === Object.prototype) {
    const out: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = isPinKey(k) ? '[REDACTED]' : redactPins(v)
    }
    return out as T
  }
  return value
}

/** Bugsnag `redactedKeys`: its default (password) plus every PIN key, so a
 *  PIN in event metadata is filtered by the SDK too, not only the
 *  breadcrumb scrub. */
export const BUGSNAG_REDACTED_KEYS: Array<string | RegExp> = ['password', PIN_KEY]
